import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  MapPin, HandCoins, FileText, Check, ArrowLeft, ArrowRight,
  Info, Fish, Mail, ShieldCheck, CheckCircle2, Send,
} from "lucide-react";
import { api, formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const FISHING_TYPES = [
  "Pêche au bar", "Pêche au lieu", "Pêche au maquereau", "Pêche à la seiche",
  "Pêche aux leurres", "Pêche en dérive", "Pêche côtière", "Pêche familiale",
];
const LEVELS = ["Familiale", "Tous niveaux", "Débutant accepté", "Intermédiaire", "Confirmé"];
const GEAR_SUGGESTIONS = ["Cannes", "Leurres", "Appâts", "Gilets", "Plombs", "Mitraillettes", "Turluttes", "Cirés"];

export default function Publish() {
  const nav = useNavigate();
  const { user, checkAuth } = useAuth();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: "",
    type: "",
    port: "",
    date: "",
    time: "07:00",
    duration_hours: 4,
    spots_total: 3,
    price_per_person: 8,
    description: "",
    level: "Tous niveaux",
    gear_provided: [],
    cost_breakdown: [
      { label: "Carburant", amount: 5 },
      { label: "Appâts & consommables", amount: 2 },
      { label: "Entretien matériel", amount: 1 },
    ],
  });

  // OTP / contact state — email only
  const [contactName, setContactName] = useState(user?.name || "");
  const [identifier, setIdentifier] = useState(user?.email || "");
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [verified, setVerified] = useState(!!user);

  useEffect(() => { document.title = "Proposer une sortie — Le Bon Matelot"; }, []);
  useEffect(() => {
    if (user) {
      setVerified(true);
      setContactName(user.name || "");
      setIdentifier(user.email || "");
    }
  }, [user]);

  const { data: ports = [] } = useQuery({
    queryKey: ["ports"], queryFn: async () => (await api.get("/ports")).data,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleGear = (g) => set("gear_provided", form.gear_provided.includes(g)
    ? form.gear_provided.filter((x) => x !== g)
    : [...form.gear_provided, g]);
  const updateCost = (i, k, v) => {
    const arr = [...form.cost_breakdown];
    arr[i] = { ...arr[i], [k]: k === "amount" ? Number(v) : v };
    set("cost_breakdown", arr);
  };
  const totalCost = form.cost_breakdown.reduce((s, c) => s + (Number(c.amount) || 0), 0);

  const totalSteps = verified ? 3 : 4;
  const progress = Math.round((step / totalSteps) * 100);

  const canNext1 = form.title && form.type && form.port;
  const canNext2 = form.date && form.duration_hours > 0 && form.spots_total > 0;
  const canPublish = form.description.length > 10;

  // ─── Trip publish helper (called after auth OR if already logged in) ───
  const publishTrip = async () => {
    setSubmitting(true);
    try {
      const isoDate = new Date(`${form.date}T${form.time}:00`).toISOString();
      const payload = {
        title: form.title, type: form.type, port: form.port,
        date: isoDate,
        duration_hours: Number(form.duration_hours),
        spots_total: Number(form.spots_total),
        price_per_person: Number(form.price_per_person || totalCost),
        description: form.description, level: form.level,
        gear_provided: form.gear_provided,
        cost_breakdown: form.cost_breakdown.filter((c) => c.label && c.amount >= 0),
      };
      const { data } = await api.post("/trips", payload);
      toast.success("Sortie publiée");
      nav(`/sortie/${data.trip_id}`);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Step actions ───
  const onSubmitContent = async () => {
    // Step 3 (description) → if user logged in, publish directly; otherwise go to step 4 (OTP)
    if (verified) {
      await publishTrip();
    } else {
      setStep(4);
    }
  };

  const requestCode = async () => {
    if (!identifier || !identifier.includes("@")) {
      toast.error("Renseignez une adresse email valide"); return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/otp/request", {
        channel: "email", identifier, name: contactName,
      });
      setCodeSent(true);
      toast.success("Code envoyé par email. Vérifiez votre boîte de réception.");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const verifyAndPublish = async () => {
    if (!code || code.length < 4) { toast.error("Saisissez le code reçu"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/otp/verify", { identifier, code });
      setVerified(true);
      await checkAuth();
      toast.success("Compte validé");
      await publishTrip();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-blanc min-h-screen">
      <div className="max-w-2xl mx-auto px-6 pt-10 pb-16">
        <div className="text-[11px] uppercase tracking-widest text-bretagne font-bold">Proposer une sortie</div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-ocean mt-1 tracking-tight">
          Embarquons ensemble.
        </h1>

        {/* Progress */}
        <div className="mt-6">
          <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest mb-2">
            <span className={step >= 1 ? "text-corail" : "text-bretagne/60"}>1 · Informations</span>
            <span className={step >= 2 ? "text-corail" : "text-bretagne/60"}>2 · Organisation</span>
            <span className={step >= 3 ? "text-corail" : "text-bretagne/60"}>3 · Description</span>
            {!verified && <span className={step >= 4 ? "text-corail" : "text-bretagne/60"}>4 · Validation</span>}
          </div>
          <div className="h-1.5 rounded-full bg-brume overflow-hidden">
            <div data-testid="wizard-progress" className="h-full bg-corail transition-all duration-500"
                 style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="mt-8 bg-blanc border border-brume rounded-2xl p-6 md:p-8">
          {/* ─── Step 1 ─── */}
          {step === 1 && (
            <div className="space-y-6" data-testid="step-1">
              <div>
                <label className="text-[11px] uppercase tracking-widest text-bretagne font-bold">Titre de la sortie</label>
                <Input data-testid="form-title" value={form.title} onChange={(e) => set("title", e.target.value)}
                  placeholder="Pêche au bar au lever du jour" className="mt-1.5 bg-brume/30 border-brume rounded-xl" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-bretagne font-bold">Type de pêche</label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {FISHING_TYPES.map((t) => (
                    <button key={t} data-testid={`type-${t}`} onClick={() => set("type", t)}
                      className={`text-left text-sm font-semibold px-3 py-2.5 rounded-xl border transition tactile ${
                        form.type === t ? "border-corail bg-corail/8 text-ocean" : "border-brume bg-blanc text-ocean/80 hover:border-bretagne/40"}`}>
                      <Fish className="inline w-3.5 h-3.5 mr-1.5 text-bretagne" /> {t}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-bretagne font-bold">Port de départ</label>
                <div className="mt-2 grid sm:grid-cols-2 gap-2">
                  {ports.map((p) => (
                    <button key={p.id} data-testid={`port-${p.id}`} onClick={() => set("port", p.name)}
                      className={`inline-flex items-center gap-2 text-sm font-semibold px-3 py-2.5 rounded-xl border transition tactile ${
                        form.port === p.name ? "border-corail bg-corail/8 text-ocean" : "border-brume bg-blanc text-ocean/80 hover:border-bretagne/40"}`}>
                      <MapPin className="w-3.5 h-3.5 text-bretagne" /> {p.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ─── Step 2 ─── */}
          {step === 2 && (
            <div className="space-y-5" data-testid="step-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-bretagne font-bold">Date</label>
                  <Input data-testid="form-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)}
                    min={new Date().toISOString().slice(0, 10)} className="mt-1.5 bg-brume/30 border-brume rounded-xl" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-bretagne font-bold">Heure</label>
                  <Input data-testid="form-time" type="time" value={form.time} onChange={(e) => set("time", e.target.value)}
                    className="mt-1.5 bg-brume/30 border-brume rounded-xl" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-bretagne font-bold">Durée (h)</label>
                  <Input data-testid="form-duration" type="number" min={1} step={0.5} value={form.duration_hours}
                    onChange={(e) => set("duration_hours", e.target.value)} className="mt-1.5 bg-brume/30 border-brume rounded-xl" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-bretagne font-bold">Places dispo</label>
                  <Input data-testid="form-spots" type="number" min={1} max={20} value={form.spots_total}
                    onChange={(e) => set("spots_total", e.target.value)} className="mt-1.5 bg-brume/30 border-brume rounded-xl" />
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-bretagne font-bold">Niveau requis</label>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {LEVELS.map((l) => (
                    <button key={l} data-testid={`level-${l}`} onClick={() => set("level", l)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition tactile ${
                        form.level === l ? "border-corail bg-corail text-blanc" : "border-brume bg-blanc text-ocean/80"}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-bretagne font-bold inline-flex items-center gap-1.5">
                  <HandCoins className="w-3 h-3" /> Répartition des frais (estimation, libre)
                </label>
                <div className="mt-2 space-y-2">
                  {form.cost_breakdown.map((c, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input value={c.label} onChange={(e) => updateCost(i, "label", e.target.value)}
                        placeholder="Carburant, appâts…" className="bg-brume/30 border-brume rounded-xl" />
                      <Input type="number" min={0} value={c.amount} onChange={(e) => updateCost(i, "amount", e.target.value)}
                        className="w-24 bg-brume/30 border-brume rounded-xl" />
                      <span className="text-sm text-bretagne shrink-0">€</span>
                    </div>
                  ))}
                  <button onClick={() => set("cost_breakdown", [...form.cost_breakdown, { label: "", amount: 0 }])}
                    className="text-xs text-corail font-bold hover:underline">+ Ajouter une ligne</button>
                </div>
                <div className="mt-3 flex items-center justify-between bg-sable border border-brume rounded-xl px-4 py-2.5">
                  <span className="text-xs uppercase tracking-widest text-bretagne font-bold">Participation / personne</span>
                  <span className="font-display text-xl font-bold text-ocean">{totalCost} €</span>
                </div>
                <div className="mt-3 bg-brume/40 border border-brume rounded-xl p-3 text-[11px] text-ocean/75 leading-relaxed flex gap-2">
                  <Info className="w-3.5 h-3.5 text-bretagne shrink-0 mt-0.5" />
                  <span>
                    Montant <strong className="text-ocean">fixé librement</strong> par vous.
                    Communiqué à titre indicatif uniquement. Le règlement s'effectue directement
                    entre les membres — aucun paiement sur la plateforme.
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* ─── Step 3 ─── */}
          {step === 3 && (
            <div className="space-y-5" data-testid="step-3">
              <div>
                <label className="text-[11px] uppercase tracking-widest text-bretagne font-bold inline-flex items-center gap-1.5">
                  <FileText className="w-3 h-3" /> Description
                </label>
                <Textarea data-testid="form-description" value={form.description}
                  onChange={(e) => set("description", e.target.value)} rows={5}
                  placeholder="Spot visé, technique, ambiance, ce qu'il faut amener…"
                  className="mt-1.5 bg-brume/30 border-brume rounded-xl" />
                <p className="text-[11px] text-bretagne/70 mt-1.5">{form.description.length} caractères · min. 10</p>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-bretagne font-bold">Matériel fourni</label>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {GEAR_SUGGESTIONS.map((g) => (
                    <button key={g} onClick={() => toggleGear(g)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition tactile inline-flex items-center gap-1 ${
                        form.gear_provided.includes(g) ? "border-turquoise bg-turquoise/15 text-turquoise" : "border-brume bg-blanc text-ocean/70"}`}>
                      {form.gear_provided.includes(g) && <Check className="w-3 h-3" />}
                      {g}
                    </button>
                  ))}
                </div>
              </div>
              <div className="bg-sable rounded-xl p-4 border border-brume text-sm">
                <div className="text-[11px] uppercase tracking-widest text-bretagne font-bold mb-1.5">Récapitulatif</div>
                <ul className="space-y-1 text-ocean/85">
                  <li><strong>{form.title || "—"}</strong> · {form.type || "—"}</li>
                  <li>{form.port} · {form.date} à {form.time} · {form.duration_hours}h</li>
                  <li>{form.spots_total} places · {form.level} · {totalCost}€ / pers.</li>
                </ul>
              </div>
            </div>
          )}

          {/* ─── Step 4 — Email OTP ─── */}
          {step === 4 && (
            <div className="space-y-5" data-testid="step-4">
              <div className="bg-ocean/8 border border-ocean/20 rounded-xl p-3 text-sm text-ocean flex gap-2">
                <ShieldCheck className="w-5 h-5 text-ocean shrink-0 mt-0.5" />
                <div>
                  Dernière étape : on valide votre <strong>adresse email</strong>.
                  Pas de mot de passe — on vous envoie un code, vous le saisissez, et votre annonce part en ligne.
                </div>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-widest text-bretagne font-bold">Votre prénom</label>
                <Input data-testid="otp-name" value={contactName} onChange={(e) => setContactName(e.target.value)}
                  placeholder="Yann" className="mt-1.5 bg-brume/30 border-brume rounded-xl" />
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-widest text-bretagne font-bold inline-flex items-center gap-1.5">
                  <Mail className="w-3 h-3" /> Adresse email
                </label>
                <Input data-testid="otp-identifier" value={identifier}
                  onChange={(e) => { setIdentifier(e.target.value); setCodeSent(false); }}
                  type="email" placeholder="vous@exemple.fr"
                  className="mt-1.5 bg-brume/30 border-brume rounded-xl" />
              </div>

              {!codeSent ? (
                <Button data-testid="otp-send" onClick={requestCode} disabled={submitting || !identifier}
                  className="w-full bg-ocean hover:bg-ocean/90 text-blanc rounded-full py-5 font-bold tactile">
                  <Send className="w-4 h-4 mr-2" /> {submitting ? "Envoi…" : "Recevoir un code par email"}
                </Button>
              ) : (
                <>
                  <div className="bg-turquoise/15 border border-turquoise/30 rounded-xl p-3 text-xs text-ocean/90 leading-relaxed inline-flex gap-2">
                    <Mail className="w-4 h-4 text-bretagne shrink-0 mt-0.5" />
                    <span>Un code à 6 chiffres a été envoyé à <strong>{identifier}</strong>. Vérifiez votre boîte de réception (et le dossier indésirables).</span>
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-widest text-bretagne font-bold">Code reçu</label>
                    <Input data-testid="otp-code" value={code} onChange={(e) => setCode(e.target.value)}
                      placeholder="6 chiffres" maxLength={6}
                      className="mt-1.5 bg-brume/30 border-brume rounded-xl font-display text-lg tracking-widest text-center" />
                  </div>
                  <Button data-testid="otp-verify" onClick={verifyAndPublish} disabled={submitting || code.length < 4}
                    className="w-full bg-ocean hover:bg-ocean/90 text-blanc rounded-full py-5 font-bold tactile shadow-matelot disabled:opacity-50">
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    {submitting ? "Publication…" : "Valider et publier mon annonce"}
                  </Button>
                  <button onClick={requestCode} className="w-full text-xs text-bretagne hover:text-ocean">
                    Renvoyer un code
                  </button>
                </>
              )}
            </div>
          )}

          {/* ─── Navigation ─── */}
          <div className="mt-8 flex justify-between gap-3">
            <Button
              data-testid="wizard-back"
              variant="ghost"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
              className="text-bretagne disabled:opacity-30"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> Retour
            </Button>
            {step < 3 ? (
              <Button
                data-testid="wizard-next"
                onClick={() => setStep((s) => s + 1)}
                disabled={(step === 1 && !canNext1) || (step === 2 && !canNext2)}
                className="bg-ocean hover:bg-ocean/90 text-blanc rounded-full px-6 py-5 font-bold tactile disabled:opacity-50"
              >
                Continuer <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : step === 3 ? (
              <Button
                data-testid="wizard-submit"
                onClick={onSubmitContent}
                disabled={!canPublish || submitting}
                className="bg-ocean hover:bg-ocean/90 text-blanc rounded-full px-6 py-5 font-bold tactile shadow-matelot disabled:opacity-50"
              >
                {verified ? (submitting ? "Publication…" : <>Publier mon annonce <Check className="w-4 h-4 ml-1" /></>)
                          : <>Publier mon annonce <ArrowRight className="w-4 h-4 ml-1" /></>}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
